import React, { useRef, useState, useCallback, useEffect } from "react";
import { Box, Typography, Snackbar, Alert } from "@mui/material";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import QrScanner from "qr-scanner";
import { useUser } from "../contexts/UserContext";
import useFetchWithMsal from "../hooks/useFetchWithMsal";
import { protectedResources } from "../authConfig";
import logger from "../logger";
import DeviceCard from "../components/DeviceCard";
import TopBar from "../components/QrCodeDeviceMapping/TopBar";
import BottomBar from "../components/QrCodeDeviceMapping/BottomBar";
import CameraDialog from "../components/QrCodeDeviceMapping/CameraDialog";
import EmailReportDialog from "../components/QrCodeDeviceMapping/EmailReportDialog";
import NotFoundDialog from "../components/QrCodeDeviceMapping/NotFoundDialog";
import FailedPatchesDialog from "../components/QrCodeDeviceMapping/FailedPatchesDialog";
import UpdateDialog from "../components/QrCodeDeviceMapping/UpdateDialog";
import {
  STORAGE_KEY,
  CAMERA_START_DELAY_MS,
  RETRY_BASE_DELAY_MS,
  REQUEST_TIMEOUT_MS,
  extractPairzonId,
  decodeQrFromFile,
  toCustName,
  isValidEmail,
} from "../components/QrCodeDeviceMapping/utils";


const QrCodeDeviceMappingPage = () => {
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const qrScannerRef = useRef(null);
  const lastScannedRef = useRef(null); // { id, timestamp } – debounce duplicate scans in camera mode
  const scanningPausedRef = useRef(false);
  const { user } = useUser();

  const [scannedItems, setScannedItems] = useState(() => {
    // Restore from localStorage on initial mount
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      logger.error("Failed to restore scanned items from localStorage", { error });
      return [];
    }
  });
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [emailDialog, setEmailDialog] = useState(false);
  const [emailAddress, setEmailAddress] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [emailDataSource, setEmailDataSource] = useState("scanned"); // "scanned" or "mongodb"
  const [emailStartDate, setEmailStartDate] = useState("");
  const [emailEndDate, setEmailEndDate] = useState("");
  const [fetchingMongoData, setFetchingMongoData] = useState(false);

  const [saveMappingSending, setSaveMappingSending] = useState(false);
  const [saveMappingSuccess, setSaveMappingSuccess] = useState(false);
  const [failedPatchesDialog, setFailedPatchesDialog] = useState(false);
  const [failedPatches, setFailedPatches] = useState([]);

  const [notFoundDialog, setNotFoundDialog] = useState(false);
  const [cameraDialogOpen, setCameraDialogOpen] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [cameraScanResult, setCameraScanResult] = useState(null); // null | { processing } | { success, sernum } | { success: false, message }
  const [scanAddedSnackbar, setScanAddedSnackbar] = useState(false);
  const [lastAddedSernum, setLastAddedSernum] = useState("");

  const [updateTargetId, setUpdateTargetId] = useState(null);
  const [updateDialog, setUpdateDialog] = useState(false);

  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customersReady, setCustomersReady] = useState(false);

  const { execute } = useFetchWithMsal({
    scopes: protectedResources.apiScopes.scopes.read,
  });

  // Save scanned items to localStorage whenever they change.
  // On unmount (page leave) clear the storage so stale items don't persist.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(scannedItems));
    } catch (error) {
      logger.error("Failed to save scanned items to localStorage", { error });
    }
    return () => {
      localStorage.removeItem(STORAGE_KEY);
    };
  }, [scannedItems]);

  // Fetch customers list once on mount
  useEffect(() => {
    const fetchCustomers = async () => {
      setCustomersLoading(true);
      try {
        const data = await execute(
          "GET",
          `${process.env.REACT_APP_BACKEND_BASE_URL}/api/qrCodeDeviceMapping/customers`,
        );
        setCustomers(Array.isArray(data) ? data : []);
        setCustomersReady(true);
      } catch (err) {
        logger.error("Failed to fetch customers on mount", { error: err });
        setCustomersReady(false);
      } finally {
        setCustomersLoading(false);
      }
    };
    fetchCustomers();
  }, [execute]);

  const handleScanClick = () => {
    setErrorMsg("");
    fileInputRef.current.click();
  };

  /** Shared QR result processor – used by both file-upload and live-camera flows. */
  const processQrText = useCallback(
    async (qrText) => {
      const pairzonId = extractPairzonId(qrText);
      if (!pairzonId) {
        throw new Error("לא נמצא מזהה מכשיר בקוד QR");
      }

      const device = await execute(
        "GET",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/qrCodeDeviceMapping/lookup?pairzonId=${pairzonId}`,
      );

      setScannedItems((prev) => {
        const alreadyExists = prev.some((i) => i.SERNUM === device.SERNUM);
        if (alreadyExists) {
          setErrorMsg(`המכשיר ${device.SERNUM} כבר נסרק`);
          return prev;
        }
        return [
          { ...device, id: pairzonId, approved: false, chariaga: false, chariagaData: null },
          ...prev,
        ];
      });

      return device;
    },
    [execute],
  );

  const handleFileChange = useCallback(
    async (e) => {
      const file = e.target.files[0];
      e.target.value = "";
      if (!file) return;

      setLoading(true);
      setErrorMsg("");

      try {
        const qrText = await decodeQrFromFile(file);
        const device = await processQrText(qrText);
        if (device) {
          setLastAddedSernum(device.SERNUM);
          setScanAddedSnackbar(true);
        }
      } catch (err) {
        logger.error("QR scan error", { error: err });
        if (err?.status === 404) {
          setNotFoundDialog(true);
        } else {
          setErrorMsg(err?.msg || err?.message || "שגיאה בעת סריקת הקוד");
        }
      } finally {
        setLoading(false);
      }
    },
    [processQrText],
  );

  // Camera QR scanner
  const openCameraScanner = useCallback(() => {
    setErrorMsg("");
    setCameraError("");
    setCameraDialogOpen(true);
  }, []);

  const startCameraScanner = useCallback(async () => {
    if (!videoRef.current) return;
    scanningPausedRef.current = false;
    try {
      const scanner = new QrScanner(
        videoRef.current,
        async (result) => {
          // Skip if currently processing a previous scan
          if (scanningPausedRef.current) return;

          const pairzonId = extractPairzonId(result.data);

          // Debounce: ignore the same QR code scanned within 3 seconds
          const now = Date.now();
          if (
            lastScannedRef.current?.id === pairzonId &&
            now - lastScannedRef.current.timestamp < 3000
          ) return;

          scanningPausedRef.current = true;
          lastScannedRef.current = { id: pairzonId, timestamp: now };

          setCameraScanResult({ processing: true });
          try {
            const device = await processQrText(result.data);
            setCameraScanResult({ success: true, sernum: device?.SERNUM ?? "" });
          } catch (err) {
            logger.error("QR camera scan error", { error: err });
            const message =
              err?.status === 404
                ? "קוד QR לא נמצא במערכת"
                : err?.msg || err?.message || "שגיאה בסריקה";
            setCameraScanResult({ success: false, message });
          } finally {
            setTimeout(() => {
              setCameraScanResult(null);
              scanningPausedRef.current = false;
            }, 1800);
          }
        },
        {
          returnDetailedScanResult: true,
          preferredCamera: "environment",
          highlightScanRegion: true,
          highlightCodeOutline: true,
          maxScansPerSecond: 5,
        },
      );
      qrScannerRef.current = scanner;
      await scanner.start();
    } catch (err) {
      logger.error("Camera access error", { error: err });
      setCameraError("לא ניתן לגשת למצלמה – אנא אשר הרשאת גישה ונסה שנית");
    }
  }, [processQrText]);

  const closeCameraScanner = useCallback(() => {
    if (qrScannerRef.current) {
      qrScannerRef.current.stop();
      qrScannerRef.current.destroy();
      qrScannerRef.current = null;
    }
    setCameraDialogOpen(false);
    setCameraError("");
  }, []);

  // Start the live scanner once the camera dialog is open
  useEffect(() => {
    if (!cameraDialogOpen) return;
    const timer = setTimeout(startCameraScanner, CAMERA_START_DELAY_MS);
    return () => clearTimeout(timer);
  }, [cameraDialogOpen, startCameraScanner]);

  // Release camera resources when the component unmounts
  useEffect(() => {
    return () => {
      if (qrScannerRef.current) {
        qrScannerRef.current.stop();
        qrScannerRef.current.destroy();
        qrScannerRef.current = null;
      }
    };
  }, []);

  const handleApprove = (id) => {
    setScannedItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, approved: true } : item)),
    );
  };

  const handleUpdate = useCallback((id) => {
    setUpdateTargetId(id);
    setUpdateDialog(true);
    setErrorMsg("");
  }, []);

  const handleLoadSites = useCallback(async (customer) => {
    const custname = toCustName(customer.ACCNAME);
    const data = await execute(
      "GET",
      `${process.env.REACT_APP_BACKEND_BASE_URL}/api/qrCodeDeviceMapping/customerSites?custname=${encodeURIComponent(custname)}`,
    );
    return Array.isArray(data) ? data : [];
  }, [execute]);

  const handleUpdateConfirm = useCallback(({ custId, customer, site }) => {
    setUpdateDialog(false);
    setScannedItems((prev) =>
      prev.map((item) => {
        if (item.id !== updateTargetId) return item;
        const updated = { ...item, ELDN_CUSTSERNUM: custId !== "" ? custId : item.ELDN_CUSTSERNUM };
        const custIdChanged = custId !== "" && custId !== item.ELDN_CUSTSERNUM;
        if (customer && site) {
          updated.chariaga = true;
          updated.chariagaData = {
            accName:     toCustName(customer.ACCNAME),
            accDes:      customer.ACCDES,
            siteCode:    site.CODE,
            siteCodeDes: site.CODEDES,
          };
        } else if (custIdChanged) {
          updated.chariaga = true;
        }
        return updated;
      }),
    );
    setUpdateTargetId(null);
  }, [updateTargetId]);

  // Retry helper for transient failures
  const executeWithRetry = async (method, url, body, retries = 2) => {
    const attempts = Array.from({ length: retries + 1 }, (_, i) => i);
    
    for (const attemptIndex of attempts) {
      try {
        return await execute(method, url, body);
      } catch (err) {
        const isLastAttempt = attemptIndex === retries;
        if (isLastAttempt) throw err;
        
        const retryNumber = attemptIndex + 1;
        logger.warn(`Retry ${retryNumber}/${retries} after error`, { error: err, url });
        
        const delayMs = RETRY_BASE_DELAY_MS * retryNumber;
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
    }
  };

  const handleOpenEmailDialog = () => {
    // Ensure user context is loaded
    if (!user?.email) {
      setErrorMsg("פרטי משתמש לא זמינים - אנא טען מחדש את הדף");
      logger.error("User context not available when opening email dialog");
      return;
    }
    
    // Reset date selection when opening dialog
    setEmailDataSource("scanned");
    setEmailStartDate("");
    setEmailEndDate("");
    setEmailDialog(true);
  };

  const handleSendReport = async () => {
    if (!isValidEmail(emailAddress)) return;

    // Validate MongoDB date selection
    if (emailDataSource === "mongodb" && (!emailStartDate || !emailEndDate)) {
      setErrorMsg("נא לבחור טווח תאריכים");
      return;
    }

    setEmailSending(true);
    setErrorMsg("");
    
    try {
      let devicesToSend = scannedItems;

      // Fetch from MongoDB if that option is selected
      if (emailDataSource === "mongodb") {
        setFetchingMongoData(true);
        
        // Use retry logic for MongoDB fetch
        const response = await executeWithRetry(
          "GET",
          `${process.env.REACT_APP_BACKEND_BASE_URL}/api/qrCodeDeviceMapping/getMappingsByDateRange?startDate=${emailStartDate}&endDate=${emailEndDate}`,
        );
        devicesToSend = response || [];
        setFetchingMongoData(false);

        if (devicesToSend.length === 0) {
          setErrorMsg("לא נמצאו נתונים בטווח התאריכים שנבחר");
          setEmailSending(false);
          return;
        }
      }

      // Add timeout wrapper to prevent hanging requests
      const timeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout')), REQUEST_TIMEOUT_MS)
      );
      
      const sendEmail = executeWithRetry(
        "POST",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/qrCodeDeviceMapping/sendReport`,
        { email: emailAddress, devices: devicesToSend },
      );

      await Promise.race([sendEmail, timeout]);
      
      setEmailDialog(false);
      setEmailAddress("");
      setEmailDataSource("scanned");
      setEmailStartDate("");
      setEmailEndDate("");
      setEmailSuccess(true);
      logger.info("Email report sent successfully", { email: emailAddress, deviceCount: devicesToSend.length });
    } catch (err) {
      logger.error("Email send error", { error: err, email: emailAddress });
      
      // Provide specific error messages based on error type
      if (err?.message === 'Request timeout') {
        setErrorMsg("הבקשה לקחה יותר מדי זמן - אנא נסה שנית");
      } else if (err?.status === 401 || err?.status === 403) {
        setErrorMsg("שגיאת הרשאה - אנא התחבר מחדש");
      } else if (err?.status === 500) {
        setErrorMsg("שגיאת שרת - אנא נסה שנית מאוחר יותר");
      } else if (!navigator.onLine) {
        setErrorMsg("אין חיבור לאינטרנט - אנא בדוק את החיבור");
      } else {
        setErrorMsg(err?.msg || err?.message || "שגיאה בשליחת הדוח");
      }
    } finally {
      setEmailSending(false);
      setFetchingMongoData(false);
    }
  };

  // Save mapping to MongoDB
  const handleSaveMapping = async () => {
    if (scannedItems.length === 0) {
      logger.debug("No items to save");
      return;
    }
    
    logger.info("Starting save mapping", { 
      itemCount: scannedItems.length, 
      userEmail: user?.email 
    });
    
    setSaveMappingSending(true);
    setErrorMsg("");
    
    try {
      const userEmail = user?.email || 'unknown@email.com';
      const userName = user?.fullName || '';

      const response = await execute(
        "POST",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/qrCodeDeviceMapping/saveMapping`,
        { userEmail, userName, devices: scannedItems },
      );
      
      logger.info("Save mapping response", { response });
      
      // Check if there are failed Priority patches
      if (response?.failedPatches && response.failedPatches.length > 0) {
        setFailedPatches(response.failedPatches);
        setFailedPatchesDialog(true);
        // Don't clear items yet, let user review failed patches
      } else {
        // Clear localStorage and scanned items after successful save
        localStorage.removeItem(STORAGE_KEY);
        setScannedItems([]);
        setSaveMappingSuccess(true);
      }
    } catch (err) {
      logger.error("Save mapping error", { error: err });
      setErrorMsg(err?.msg || err?.message || "שגיאה בשמירת המיפוי");
    } finally {
      setSaveMappingSending(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#f0f4f8",
        direction: "rtl",
        pb: "158px",
      }}
    >
      <TopBar
        customersLoading={customersLoading}
        customersReady={customersReady}
        scannedCount={scannedItems.length}
      />

      {/* Scanned items list */}
      <Box
        sx={{
          flex: 1,
          px: 2,
          pt: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        {scannedItems.length === 0 && !loading && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              mt: 8,
              gap: 2,
              color: "#aaa",
            }}
          >
            <QrCodeScannerIcon sx={{ fontSize: 72, opacity: 0.3 }} />
            <Typography sx={{ fontSize: "1rem" }}>
              לחץ על כפתור הסריקה כדי להתחיל
            </Typography>
          </Box>
        )}

        {scannedItems.map((item) => (
          <DeviceCard
            key={item.id}
            item={item}
            onApprove={handleApprove}
            onUpdate={handleUpdate}
            updateDisabled={!customersReady}
          />
        ))}
      </Box>

      {/* Error message */}
      {errorMsg && (
        <Box
          sx={{
            mx: 2,
            mt: 1,
            mb: 1,
            p: 1.5,
            bgcolor: "#ffebee",
            borderRadius: 2,
            border: "1px solid #ef9a9a",
          }}
        >
          <Typography sx={{ color: "#c62828", fontSize: "0.88rem", textAlign: "center" }}>
            {errorMsg}
          </Typography>
        </Box>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      <BottomBar
        loading={loading}
        scannedCount={scannedItems.length}
        saveMappingSending={saveMappingSending}
        onScanClick={openCameraScanner}
        onEmailClick={handleOpenEmailDialog}
        onSaveClick={handleSaveMapping}
      />

      <CameraDialog
        open={cameraDialogOpen}
        onClose={closeCameraScanner}
        cameraError={cameraError}
        cameraScanResult={cameraScanResult}
        videoRef={videoRef}
        onGalleryClick={handleScanClick}
      />

      <UpdateDialog
        open={updateDialog}
        onClose={() => { setUpdateDialog(false); setUpdateTargetId(null); }}
        onConfirm={handleUpdateConfirm}
        customers={customers}
        customersLoading={customersLoading}
        onLoadSites={handleLoadSites}
        initialCustSernum={scannedItems.find((i) => i.id === updateTargetId)?.ELDN_CUSTSERNUM || ""}
      />

      <EmailReportDialog
        open={emailDialog}
        onClose={() => {
          if (!emailSending && !fetchingMongoData) {
            setEmailDialog(false);
            setEmailDataSource("scanned");
            setEmailStartDate("");
            setEmailEndDate("");
          }
        }}
        onCancel={() => {
          setEmailDialog(false);
          setEmailAddress("");
          setEmailDataSource("scanned");
          setEmailStartDate("");
          setEmailEndDate("");
        }}
        onSend={handleSendReport}
        emailAddress={emailAddress}
        setEmailAddress={setEmailAddress}
        emailSending={emailSending}
        fetchingMongoData={fetchingMongoData}
        emailDataSource={emailDataSource}
        setEmailDataSource={setEmailDataSource}
        emailStartDate={emailStartDate}
        setEmailStartDate={setEmailStartDate}
        emailEndDate={emailEndDate}
        setEmailEndDate={setEmailEndDate}
        scannedCount={scannedItems.length}
      />

      <NotFoundDialog
        open={notFoundDialog}
        onClose={() => setNotFoundDialog(false)}
      />

      <FailedPatchesDialog
        open={failedPatchesDialog}
        onClose={() => setFailedPatchesDialog(false)}
        failedPatches={failedPatches}
        onAcknowledge={() => {
          setFailedPatchesDialog(false);
          localStorage.removeItem(STORAGE_KEY);
          setScannedItems([]);
          setFailedPatches([]);
        }}
      />

      <Snackbar
        open={emailSuccess}
        autoHideDuration={4000}
        onClose={() => setEmailSuccess(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setEmailSuccess(false)}
          severity="success"
          variant="filled"
          sx={{ borderRadius: 2, fontWeight: 600, direction: "rtl" }}
        >
          הדוח נשלח בהצלחה!
        </Alert>
      </Snackbar>
      <Snackbar
        open={saveMappingSuccess}
        autoHideDuration={15000}
        onClose={() => setSaveMappingSuccess(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ marginTop: "80px" }}
      >
        <Alert
          onClose={() => setSaveMappingSuccess(false)}
          severity="success"
          variant="filled"
          sx={{ borderRadius: 2, fontWeight: 600, direction: "ltr" }}
        >
          המיפוי נשמר בהצלחה!
        </Alert>
      </Snackbar>
      <Snackbar
        open={scanAddedSnackbar}
        autoHideDuration={3000}
        onClose={() => setScanAddedSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ marginTop: "80px" }}
      >
        <Alert
          onClose={() => setScanAddedSnackbar(false)}
          severity="success"
          variant="filled"
          sx={{ borderRadius: 2, fontWeight: 600, direction: "rtl" }}
        >
          {`המכשיר ${lastAddedSernum} נוסף בהצלחה`}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default QrCodeDeviceMappingPage;
